import dayjs from "dayjs";
import type React from "react";
import { useCallback, useEffect, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CustomDatePicker } from "@/components/common/CustomDatePicker";
import type { RecurrenceRule } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";
import {
  dayjsWeekdayToRecurrence,
  formatSetPosLabel,
  formatWeekdayLabel,
  getAllowedSetPositions,
  getMonthlyWeekInfo,
  getRecurrenceGenerationLimit,
  recurrenceWeekdayToDayjsIndex,
  validateRecurrenceRule,
} from "@/lib/utils/event/recurrence";

interface RecurrenceEditorProps {
  value: RecurrenceRule | null | undefined;
  startDate: string;
  endDate?: string;
  minEndDate?: string;
  onChange: (rule: RecurrenceRule | null) => void;
  errors?: string[];
  className?: string;
}

export const RecurrenceEditor: React.FC<RecurrenceEditorProps> = ({
  value,
  startDate,
  endDate,
  minEndDate,
  onChange,
  errors,
  className,
}) => {
  const { t } = useTranslation();
  const endDateId = useId();
  const recurrenceIntervalId = useId();
  const monthlyPatternId = useId();
  const freq = value?.freq ?? null;
  const minDate = useMemo(() => {
    const candidate = minEndDate ?? startDate;
    return candidate && dayjs(candidate).isValid() ? candidate : "";
  }, [minEndDate, startDate]);
  const startDateObj = useMemo(
    () => (startDate ? dayjs(startDate) : null),
    [startDate],
  );
  const startWeekday = useMemo(
    () =>
      startDateObj?.isValid()
        ? dayjsWeekdayToRecurrence(startDateObj.day())
        : "MO",
    [startDateObj],
  );
  const startWeekInfo = useMemo(
    () => (startDateObj ? getMonthlyWeekInfo(startDateObj) : null),
    [startDateObj],
  );
  const startWeekOrdinal = startWeekInfo?.weekIndex ?? 1;
  const startSetPos = startWeekInfo?.weekIndex ?? 1;
  const isLastWeek = useMemo(
    () => startWeekInfo?.isLast ?? false,
    [startWeekInfo],
  );
  const allowedSetPositions = useMemo(
    () =>
      startWeekInfo
        ? getAllowedSetPositions(startWeekInfo.weekIndex, startWeekInfo.isLast)
        : [],
    [startWeekInfo],
  );
  const startMonthDay = useMemo(
    () => (startDateObj?.isValid() ? startDateObj.date() : 1),
    [startDateObj],
  );

  const monthlyMode: "byMonthDay" | "byWeekday" =
    freq === "MONTHLY" && (value?.byDay || value?.bySetPos)
      ? "byWeekday"
      : "byMonthDay";

  const monthlyPatternOptions = useMemo(() => {
    const weekdayLabel = formatWeekdayLabel(startWeekday, t);
    const setPosValueForLabel = isLastWeek ? startWeekOrdinal : startSetPos;
    const options: Array<{
      value: "date" | "setpos" | "last";
      label: string;
      pos?: number;
    }> = [
      {
        value: "date",
        label: t("common.recurrencePatternDateOption", {
          defaultValue: "Same date ({day})",
          day: startMonthDay,
        }),
      },
      {
        value: "setpos",
        label: t("common.recurrencePatternSetPosOption", {
          defaultValue: "{pos} {weekday}",
          pos: formatSetPosLabel(setPosValueForLabel, t),
          weekday: weekdayLabel,
        }),
        pos: setPosValueForLabel,
      },
    ];
    if (isLastWeek) {
      options.push({
        value: "last",
        label: t("common.recurrencePatternLastOption", {
          defaultValue: "Last {weekday}",
          weekday: weekdayLabel,
        }),
        pos: -1,
      });
    }
    return options;
  }, [
    startWeekday,
    startMonthDay,
    startSetPos,
    isLastWeek,
    t,
    startWeekOrdinal,
  ]);

  const selectedPattern: "date" | "setpos" | "last" = (() => {
    if (monthlyMode === "byMonthDay") return "date";
    if (value?.bySetPos === -1 && isLastWeek) return "last";
    return "setpos";
  })();

  const weeklyLabel = useMemo(() => {
    const weekdayLabel = formatWeekdayLabel(startWeekday, t);
    return startDate
      ? t("common.weeklyWithDay", {
          defaultValue: "Weekly ({weekday})",
          weekday: weekdayLabel,
        })
      : t("common.weekly");
  }, [startDate, startWeekday, t]);

  const derivedErrors = useMemo(() => {
    const result = validateRecurrenceRule(
      value,
      startDate,
      endDate,
      t,
      minDate,
    );
    return result.errors;
  }, [value, startDate, endDate, t, minDate]);

  const clampUntilToMin = useCallback(
    (until: string) => {
      if (!minDate) return until;
      const untilDate = dayjs(until);
      const min = dayjs(minDate);
      if (!untilDate.isValid() || !min.isValid()) return until;
      return untilDate.isBefore(min, "day") ? min.format("YYYY-MM-DD") : until;
    },
    [minDate],
  );

  const renderErrors = errors && errors.length > 0 ? errors : derivedErrors;

  const updateRule = useCallback(
    (updates: Partial<RecurrenceRule>) => {
      const base: RecurrenceRule =
        value ??
        ({ freq: "DAILY", interval: 1, until: startDate } as RecurrenceRule);
      const next = { ...base, ...updates } as RecurrenceRule;
      // end by count は非対応なので count は常に落とし、until を必ず持たせる
      if (!next.until) {
        next.until = startDate;
      }
      delete (next as Partial<RecurrenceRule>).count;
      onChange(next);
    },
    [value, startDate, onChange],
  );

  const computeMonthlyUntilForMonth = useCallback(
    (monthDate: string) => {
      const selectedMonth = dayjs(monthDate);
      if (!selectedMonth.isValid()) return null;
      if (!value) return selectedMonth.format("YYYY-MM-DD");

      const startDateObj = dayjs(startDate);
      const startDay = startDateObj.isValid()
        ? startDateObj.date()
        : selectedMonth.date();

      if (value.byMonthDay || (!value.byDay && value.bySetPos === undefined)) {
        const targetDay = value.byMonthDay ?? startDay;
        if (targetDay > selectedMonth.daysInMonth()) {
          return null;
        }
        return selectedMonth.date(targetDay).format("YYYY-MM-DD");
      }

      if (
        value.byDay &&
        value.bySetPos !== undefined &&
        value.bySetPos !== null
      ) {
        const targetWeekday = recurrenceWeekdayToDayjsIndex(value.byDay);
        if (value.bySetPos === -1) {
          let date = selectedMonth.endOf("month");
          while (date.day() !== targetWeekday) {
            date = date.subtract(1, "day");
          }
          return date.format("YYYY-MM-DD");
        }
        const targetPos = value.bySetPos;
        let date = selectedMonth.startOf("month");
        while (date.day() !== targetWeekday) {
          date = date.add(1, "day");
        }
        const candidate = date.add(targetPos - 1, "week");
        if (candidate.month() !== selectedMonth.month()) return null;
        return candidate.format("YYYY-MM-DD");
      }

      return selectedMonth.format("YYYY-MM-DD");
    },
    [startDate, value],
  );

  const handleUntilChange = useCallback(
    (date: string) => {
      if (!date) {
        updateRule({ until: startDate });
        return;
      }
      if (freq === "MONTHLY") {
        const computed = computeMonthlyUntilForMonth(date);
        if (computed) {
          updateRule({ until: computed });
        }
        return;
      }
      updateRule({ until: date });
    },
    [computeMonthlyUntilForMonth, freq, startDate, updateRule],
  );

  const handleFreqChange = (newFreq: RecurrenceRule["freq"]) => {
    if (!value || value.freq !== newFreq) {
      const baseUntil = value?.until ?? startDate;
      const nextUntil = clampUntilToMin(baseUntil);
      if (newFreq === "WEEKLY") {
        const startWeekday =
          (startDateObj?.isValid() &&
            dayjsWeekdayToRecurrence(startDateObj.day())) ||
          "MO";
        onChange({
          freq: newFreq,
          interval: 1,
          byDay: startWeekday,
          until: nextUntil,
        });
      } else if (newFreq === "MONTHLY") {
        onChange({
          freq: newFreq,
          interval: 1,
          byMonthDay: startMonthDay,
          until: nextUntil,
        });
      } else {
        onChange({ freq: newFreq, interval: 1, until: nextUntil });
      }
      return;
    }
  };

  const handleMonthlyPatternChange = (pattern: "date" | "setpos" | "last") => {
    if (pattern === "date") {
      onChange({
        freq: "MONTHLY",
        interval: 1,
        byMonthDay: startMonthDay,
        until: clampUntilToMin(value?.until ?? startDate),
      });
      return;
    }
    const setPosValue =
      pattern === "last" ? -1 : (allowedSetPositions[0] ?? startSetPos);
    onChange({
      freq: "MONTHLY",
      interval: 1,
      byDay: startWeekday,
      bySetPos: setPosValue,
      until: clampUntilToMin(value?.until ?? startDate),
    });
  };

  const maxDate = useMemo(() => {
    if (!startDate || !freq) return undefined;
    const limit = getRecurrenceGenerationLimit(startDate, freq);
    return limit.format("YYYY-MM-DD");
  }, [startDate, freq]);

  useEffect(() => {
    if (!value?.until || !minDate) return;
    const untilDate = dayjs(value.until);
    const min = dayjs(minDate);
    if (!untilDate.isValid() || !min.isValid()) return;
    if (untilDate.isBefore(min, "day")) {
      updateRule({ until: min.format("YYYY-MM-DD") });
    }
  }, [minDate, updateRule, value?.until]);

  useEffect(() => {
    if (!value || !startDateObj?.isValid()) return;
    if (value.freq === "WEEKLY") {
      const targetDay = dayjsWeekdayToRecurrence(startDateObj.day());
      if (!value.byDay || value.byDay !== targetDay) {
        updateRule({ byDay: targetDay });
      }
    }
    if (value.freq === "MONTHLY") {
      const updates: Partial<RecurrenceRule> = {};
      if (monthlyMode === "byWeekday") {
        if (!value.byDay || value.byDay !== startWeekday) {
          updates.byDay = startWeekday;
        }
        if (
          value.bySetPos === undefined ||
          value.bySetPos === null ||
          (allowedSetPositions.length > 0 &&
            !allowedSetPositions.includes(value.bySetPos))
        ) {
          updates.bySetPos = allowedSetPositions[0] ?? startSetPos;
        }
        if (value.byMonthDay !== undefined) {
          updates.byMonthDay = undefined;
        }
      } else {
        if (!value.byMonthDay || value.byMonthDay !== startMonthDay) {
          updates.byMonthDay = startMonthDay;
        }
        if (value.byDay !== undefined) {
          updates.byDay = undefined;
        }
        if (value.bySetPos !== undefined) {
          updates.bySetPos = undefined;
        }
      }
      if (Object.keys(updates).length > 0) {
        updateRule(updates);
      }
    }
  }, [
    value,
    startDateObj,
    monthlyMode,
    startWeekday,
    startSetPos,
    startMonthDay,
    allowedSetPositions,
    updateRule,
  ]);

  return (
    <div
      className={cn("space-y-3 rounded border border-gray-200 p-4", className)}
    >
      <div className="flex flex-col gap-2">
        <label htmlFor={recurrenceIntervalId} className="text-sm font-medium">
          {t("common.recurrenceInterval", "Interval")}
        </label>
        <select
          id={recurrenceIntervalId}
          className="w-full rounded border px-2 py-1 text-sm"
          value={freq ?? "DAILY"}
          onChange={(e) =>
            handleFreqChange(e.target.value as RecurrenceRule["freq"])
          }
        >
          <option value="DAILY">{t("common.daily")}</option>
          <option value="WEEKLY">{weeklyLabel}</option>
          <option value="MONTHLY">{t("common.monthly")}</option>
        </select>
      </div>

      {freq === "MONTHLY" && (
        <div className="space-y-3">
          <div className="flex flex-col gap-2">
            <label htmlFor={monthlyPatternId} className="text-sm font-medium">
              {t("common.recurrenceMonthlyPattern", "Monthly pattern")}
            </label>
            <select
              id={monthlyPatternId}
              className="w-full rounded border px-2 py-1 text-sm"
              value={selectedPattern}
              onChange={(e) =>
                handleMonthlyPatternChange(
                  e.target.value as "date" | "setpos" | "last",
                )
              }
            >
              {monthlyPatternOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {freq && (
        <div className="flex flex-col gap-2">
          <label htmlFor={endDateId} className="text-sm">
            {t("common.recurrenceEndDate")}
            <span className="ml-2 text-xs text-gray-500">
              {freq === "DAILY" && t("common.recurrenceLimitDaily")}
              {freq === "WEEKLY" && t("common.recurrenceLimitWeekly")}
              {freq === "MONTHLY" && t("common.recurrenceLimitMonthly")}
            </span>
          </label>
          <CustomDatePicker
            id={endDateId}
            ariaLabel={t("common.recurrenceEndDate")}
            value={value?.until ?? startDate}
            onChange={handleUntilChange}
            className="w-full"
            side="top"
            minDate={minDate}
            maxDate={maxDate}
            disabled={!startDate}
            isDateDisabled={(date) => {
              if (!freq) return false;
              if (freq === "WEEKLY" && value?.byDay) {
                const targetWeekday = recurrenceWeekdayToDayjsIndex(
                  value.byDay,
                );
                return date.day() !== targetWeekday;
              }
              if (freq === "MONTHLY") {
                if (value?.byMonthDay) {
                  return date.date() !== value.byMonthDay;
                }
                if (
                  value?.byDay &&
                  value.bySetPos !== undefined &&
                  value.bySetPos !== null
                ) {
                  const targetWeekday = recurrenceWeekdayToDayjsIndex(
                    value.byDay,
                  );
                  if (date.day() !== targetWeekday) return true;
                  const { weekIndex, isLast } = getMonthlyWeekInfo(date) ?? {
                    weekIndex: null,
                    isLast: false,
                  };
                  if (weekIndex === null) return true;
                  if (value.bySetPos === -1) {
                    return !isLast;
                  }
                  return weekIndex !== value.bySetPos;
                }
              }
              return false;
            }}
            showMonthPicker={freq === "MONTHLY"}
          />
        </div>
      )}

      {renderErrors.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-xs text-red-600">
          {renderErrors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
